import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ExternalLink,
  Monitor,
  Settings,
  ShieldAlert,
  Siren,
  Volume2,
  X,
} from 'lucide-react';
import './EmergencyBoard.css';

// HS product photographs and links are retained from the current website.
const mainProducts = [
  {
    number: '01',
    title: '緊急出動案内看板【自立式】',
    description: 'LED表示・音声・緊急出動灯で周囲に注意を促す、自立式の警報表示板です。',
    image: '/images/emergency-board/standalone-board.png',
    to: '/products/emergency-board/standalone',
  },
  {
    number: '02',
    title: '緊急出動案内看板【ポール式】',
    description: '緊急出動灯やLED表示により、緊急車両の出動を周囲に知らせるポール式警報表示板。',
    image: '/images/emergency-board/pole-board.png',
    to: '/contact',
  },
];

const systemFunctions = [
  { number: '01', Icon: Monitor, title: 'LED表示', description: '「出動中」などをLEDで表示し、周囲へ注意を促します。' },
  { number: '02', Icon: Siren, title: '緊急出動灯', description: '警告灯の光で緊急車両の出動を知らせます。' },
  { number: '03', Icon: Volume2, title: '音声アナウンス', description: '音声案内で歩行者や一般車両へ注意を促します。' },
  { number: '04', Icon: Settings, title: '専用制御', description: '設備の仕様に応じて、表示・警告灯・音声を連動させます。' },
];

const relatedEquipment = [
  {
    title: 'LED電光掲示板',
    description: '「出動中」「緊急車両接近」などをLEDで表示します。',
    image: '/images/emergency-board/gallery/eb-pole.png',
    file: '/images/emergency-board/led-display-board.png',
  },
  {
    title: '専用コントローラー HS-201',
    description: 'LED表示・警告灯・音声を連動させる専用機器です。',
    image: '/images/emergency-board/gallery/eb-controller-hs201.jpg',
    file: '/images/emergency-board/hs-201-controller.jpg',
  },
  {
    title: '緊急出動灯・音声システム',
    description: '警告灯と音声案内で、周囲に注意を促します。',
    icon: Siren,
    inquiry: true,
  },
  {
    title: '特注対応',
    description: '設置条件に合わせて、表示内容や制御方法を検討します。',
    icon: Settings,
    inquiry: true,
  },
];

const projectPhotos = [
  { image: '/images/emergency-board/work-01.png', title: '緊急出動案内看板【自立式】' },
  { image: '/images/emergency-board/work-03.png', title: '緊急出動案内看板【自立式】' },
  { image: '/images/emergency-board/gallery/eb-standalone-day.png', title: '緊急出動案内看板【自立式】・施工中' },
  { image: '/images/emergency-board/gallery/eb-standalone-night.png', title: '緊急出動案内看板【自立式】・夜間点灯' },
];



const capabilities = [
  { number: '01', title: '現地確認・仕様検討', description: '設置位置、視認性、電源・配線経路などを確認します。' },
  { number: '02', title: '表示・連動方法の検討', description: 'LED表示、緊急出動灯、音声などの構成を検討します。' },
  { number: '03', title: '製作・設置工事', description: '設置条件や採用仕様に合わせて、必要な製作・設置工事を進めます。' },
  { number: '04', title: '動作確認・更新のご相談', description: '設置後の動作確認や、既設設備の更新についてご相談ください。' },
];

const process = [
  { number: '01', title: 'お問い合わせ', description: '設置予定場所やご希望の内容をお知らせください。' },
  { number: '02', title: '条件確認・お打ち合わせ', description: '設置条件や運用方法を確認し、必要な仕様を整理します。' },
  { number: '03', title: 'ご提案・お見積り', description: '確認した条件をもとに、構成案とお見積りをご案内します。' },
  { number: '04', title: '製作・施工・確認', description: '仕様確定後、製作・設置・動作確認を進めます。' },
];

function EquipmentImage({ src, alt, className = '', fallbackLabel = '製品画像準備中', imageStyle }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <div className={`eb2-image-placeholder ${className}`} role="img" aria-label={fallbackLabel}>
        <ShieldAlert size={24} strokeWidth={1.4} aria-hidden="true" />
        <span>{fallbackLabel}</span>
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} style={imageStyle} loading="lazy" onError={() => setFailed(true)} />;
}

export default function EmergencyBoard() {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const oldTitle = document.title;
    document.title = '緊急出動警報板・緊急出動案内看板｜株式会社エイチエス';
    let meta = document.querySelector('meta[name="description"]');
    const added = !meta;
    const oldContent = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', '消防署などの緊急車両出入口に設置する緊急出動警報板・緊急出動案内看板。自立式・ポール式、LED表示、緊急出動灯、音声案内、設置工事のご相談は株式会社エイチエスへ。');
    return () => {
      document.title = oldTitle;
      if (added) meta.remove();
      else if (oldContent === null) meta.removeAttribute('content');
      else meta.setAttribute('content', oldContent);
    };
  }, []);

  useEffect(() => {
    if (!selectedPhoto) return undefined;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedPhoto(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = priorOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedPhoto]);

  return (
    <main className="eb2-page">
      {/* HERO — approved two-column composition */}
      <section className="eb2-hero eb2-hero-impact">
        <div className="eb2-container eb2-hero-impact-inner">
          <div className="eb2-hero-content">
            <span className="eb2-eyebrow">EMERGENCY WARNING SYSTEM</span>
            <h1>緊急出動警報板</h1>
            <div className="eb2-title-rule" />
            <p className="eb2-hero-lead">緊急車両の出動を、<br />周囲に知らせる警報表示システム。</p>
            <p className="eb2-hero-description">消防署の出入口などに設置し、緊急出動時に歩行者や一般車両へ注意を促します。設置場所や運用方法に合わせた機器構成をご提案します。</p>
            <div className="eb2-hero-actions">
              <a href="#products" className="eb2-button eb2-button-primary">製品・システムを見る <ArrowRight size={16} /></a>
              <Link to="/contact" className="eb2-button eb2-button-outline">お問い合わせ <ArrowRight size={16} /></Link>
            </div>
          </div>
          <div className="eb2-impact-visual">
            <img className="eb2-impact-scene" src="/images/emergency-board/pole-board.png" alt="緊急出動案内看板と消防車の設置状況" />
            <span className="eb2-impact-caption">緊急出動案内システム</span>
          </div>
        </div>
      </section>

      {/* SYSTEM OVERVIEW */}
      <section className="eb2-section" id="system">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">SYSTEM OVERVIEW</span>
            <h2>緊急出動を知らせるシステム</h2>
            <p>LED表示・緊急出動灯・音声などを組み合わせ、緊急出動時の注意喚起に使用します。</p>
          </div>
          <div className="eb2-system-grid">
            {systemFunctions.map(({ number, Icon, title, description }) => (
              <article className="eb2-system-card" key={number}>
                <div className="eb2-system-top"><span>{number}</span><Icon size={25} strokeWidth={1.6} aria-hidden="true" /></div>
                <h3>{title}</h3><p>{description}</p>
              </article>
            ))}
          </div>
          <p className="eb2-note">※使用する機器や機能、連動方法は採用仕様によって異なります。</p>
        </div>
      </section>

      {/* MAIN PRODUCTS — larger photograph / balanced horizontal rows */}
      <section className="eb2-section eb2-section-soft" id="products">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">MAIN PRODUCTS</span>
            <h2>緊急出動案内看板</h2>
            <p>設置環境に合わせて、自立式・ポール式などの設置方式をご案内します。</p>
          </div>
          <div className="eb2-main-grid">
            {mainProducts.map((product) => (
              <article className="eb2-main-card" key={product.number}>
                <div className="eb2-main-photo"><EquipmentImage src={product.image} alt={product.title} /></div>
                <div className="eb2-main-body">
                  <span className="eb2-card-number">TYPE {product.number}</span>
                  <h3>{product.title}</h3>
                  <p>{product.description}</p>
                  <Link to={product.to} className="eb2-text-link">
                    {product.number === '01' ? '製品詳細を見る' : '仕様について相談する'} <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* EQUIPMENT — 2 x 2 cards with better image proportions */}
      <section className="eb2-section" id="equipment">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">EQUIPMENT</span>
            <h2>関連機器・特注対応</h2>
            <p>表示・警告・制御など、運用方法に応じた機器構成を検討します。</p>
          </div>
          <div className="eb2-equipment-grid">
            {relatedEquipment.map((item) => (
              <article className={`eb2-equipment-card ${item.inquiry ? 'eb2-equipment-card-text' : ''}`} key={item.title}>
                {item.image ? (
                  <div className="eb2-equipment-photo">
                    <EquipmentImage src={item.image} alt={item.title} />
                  </div>
                ) : (
                  <div className="eb2-equipment-symbol" aria-hidden="true">
                    {item.icon && <item.icon size={29} strokeWidth={1.5} />}
                  </div>
                )}
                <div className="eb2-equipment-body">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  {item.file && (
                    <a href={item.file} target="_blank" rel="noopener noreferrer" className="eb2-text-link">
                      資料を見る <ExternalLink size={15} />
                    </a>
                  )}
                  {item.inquiry && (
                    <Link to="/contact" className="eb2-text-link">
                      仕様について相談する <ArrowRight size={15} />
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTION VIDEO — future manufacturing photos can be added here */}
      <section className="eb2-section eb2-media-section" id="media">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">PRODUCTION</span>
            <h2>製作・動作確認の様子</h2>
            <p>緊急出動案内看板の製作現場をご紹介します。</p>
          </div>
          <div className="eb2-production-video-wrap eb2-production-video-centered">
            <article className="eb2-media-video-card">
              <div className="eb2-media-video-frame">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  aria-label="緊急出動案内看板の製作・動作確認動画"
                >
                  <source
                    src="/videos/emergency-board/eb-factory-place-names-only.mp4"
                    type="video/mp4"
                  />
                  お使いのブラウザは動画再生に対応していません。
                </video>
              </div>
              <div className="eb2-media-caption">
                <h3>自立式案内看板の製作・動作確認</h3>
                <p>工場での本体外観と点灯確認の様子をご紹介します。</p>
              </div>
            </article>
          </div>
          <p className="eb2-media-note">※写真・動画の一部は、納入先の所在地が特定されないよう地名等を加工しています。</p>
        </div>
      </section>

      {/* TECHNICAL INFORMATION */}
      <section className="eb2-section eb2-technical">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">TECHNICAL INFORMATION</span>
            <h2>製品仕様・技術資料</h2>
            <p>製品の仕様や設置条件について、ご検討に必要な情報をご案内します。</p>
          </div>
          <div className="eb2-technical-list">
            <article className="eb2-technical-item">
              <div className="eb2-technical-content">
                <span className="eb2-technical-type">SELF-STANDING TYPE</span>
                <h3>緊急出動案内看板【自立式】</h3>
                <p>HS-ST5100 / HS-SC3500 / HS-SC3500D</p>
              </div>
              <Link to="/products/emergency-board/standalone" className="eb2-technical-link">製品詳細・仕様を見る <ArrowRight size={16} /></Link>
            </article>
            <article className="eb2-technical-item">
              <div className="eb2-technical-content">
                <span className="eb2-technical-type">CONTROLLER</span>
                <h3>専用コントローラー HS-201</h3>
                <p>制御方法やシステム構成については、ご希望の運用内容を確認のうえご案内します。</p>
              </div>
              <Link to="/contact" className="eb2-technical-link">仕様について問い合わせる <ArrowRight size={16} /></Link>
            </article>
          </div>
          <p className="eb2-technical-note">※製品仕様・制御方法・設置条件は、機種や採用構成によって異なります。</p>
        </div>
      </section>

      {/* INSTALLATION WORKS — approved featured gallery retained */}
      <section className="eb2-section eb2-section-soft" id="works">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">HS PROJECT WORKS</span>
            <h2>施工事例</h2>
            <p>緊急出動案内看板の設置事例をご紹介します。写真をクリックすると拡大表示できます。</p>
          </div>
          <div className="eb2-works-grid eb2-works-featured">
            {projectPhotos.map((project, index) => (
              <article className={`eb2-work-card ${index === 0 ? 'eb2-work-card-featured' : ''}`} key={project.image}>
                <button type="button" className="eb2-work-photo eb2-work-photo-button" onClick={() => setSelectedPhoto(project)} aria-label={`施工事例 ${index + 1} の写真を拡大する`}>
                  <EquipmentImage src={project.image} alt={`${project.title} 施工事例 ${index + 1}`} fallbackLabel="施工写真準備中" />
                  <span className="eb2-work-zoom">拡大表示</span>
                </button>
                <div className="eb2-work-body"><span>WORK {String(index + 1).padStart(2, '0')}</span><h3>{project.title}</h3></div>
              </article>
            ))}
          </div>
          <div className="eb2-works-actions">
            <Link to="/works" className="eb2-text-link">施工実績一覧を見る <ArrowRight size={16} /></Link>
            <Link to="/products/emergency-board/standalone" className="eb2-text-link">自立式の製品詳細を見る <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* HS CAPABILITIES */}
      <section className="eb2-section eb2-capabilities">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">HS CAPABILITIES</span>
            <h2>設置環境に合わせた設計・製作・施工</h2>
            <p>消防署の出入口や周辺道路の状況、運用方法を確認し、必要な設備構成と施工内容を検討します。</p>
          </div>
          <div className="eb2-capabilities-grid">
            {capabilities.map((item) => (
              <article className="eb2-capability-card" key={item.number}>
                <span>{item.number}</span><h3>{item.title}</h3><p>{item.description}</p>
              </article>
            ))}
          </div>
          <div className="eb2-capabilities-cta">
            <p>緊急出動警報板の新設・更新をご検討の方へ</p>
            <Link to="/contact" className="eb2-button eb2-button-primary">仕様・お見積りについて相談する <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* PROJECT PROCESS */}
      <section className="eb2-section">
        <div className="eb2-container">
          <div className="eb2-heading">
            <span className="eb2-eyebrow">PROCESS</span>
            <h2>ご相談から施工まで</h2>
            <p>お問い合わせからお見積り、製作・施工までの基本的な流れをご案内します。</p>
          </div>
          <div className="eb2-process-grid">
            {process.map((step) => (
              <article className="eb2-process-card" key={step.number}>
                <span className="eb2-process-number">{step.number}</span>
                <h3>{step.title}</h3><p>{step.description}</p>
              </article>
            ))}
          </div>
          <div className="eb2-support-link"><Link to="/construction-support">施工・サポートについて <ArrowRight size={16} /></Link></div>
        </div>
      </section>

      {/* CONTACT — refined for quotations */}
      <section className="eb2-contact" id="inquiry">
        <div className="eb2-container eb2-contact-inner">
          <div className="eb2-contact-details">
            <span className="eb2-eyebrow">CONTACT</span>
            <h2>緊急出動警報板の新設・更新のご相談</h2>
            <p>新規設置・既設設備の更新・仕様確認・お見積りについて、お気軽にお問い合わせください。</p>
            <div className="eb2-inquiry-guide">
              <strong>お問い合わせ時にお知らせいただきたい内容</strong>
              <p>設置予定場所／新設・更新の別／ご希望の表示内容・連動方法</p>
              <small>※詳細が決まっていない段階でもご相談いただけます。</small>
            </div>
          </div>
          <Link to="/contact" className="eb2-button eb2-button-primary eb2-contact-action">仕様・お見積りを相談する <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* PHOTO LIGHTBOX */}
      {selectedPhoto && (
        <div className="eb2-lightbox" role="presentation" onClick={() => setSelectedPhoto(null)}>
          <div className="eb2-lightbox-content" role="dialog" aria-modal="true" aria-label="施工写真の拡大表示" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="eb2-lightbox-close" onClick={() => setSelectedPhoto(null)} aria-label="閉じる"><X size={23} /></button>
            <img src={selectedPhoto.image} alt={selectedPhoto.title} />
            <p>{selectedPhoto.title}</p>
          </div>
        </div>
      )}
    </main>
  );
}
